/**
 * NivOverviewStats — the overview strip shared by the morning briefing and
 * the clinics tab. Four compact "production" tiles (clinics / money /
 * activity / people): square icon chip + label on one line, one headline
 * number, a thin segmented bar and a 2×2 breakdown, then a muted foot line.
 * A strip of attention chips follows. Display-only; every number comes from
 * buildOverviewStats() over the clinics-overview rows (same data as before
 * the Oct 2026 compact redesign).
 */

import { useMemo } from "react";
import {
  Buildings,
  ChatCircleDots,
  CurrencyCircleDollar,
  HandCoins,
  Pulse,
  UsersThree,
  Clock,
} from "@phosphor-icons/react";
import useCountUp from "./hooks/useCountUp";
import { buildOverviewStats, formatShekels } from "./nivDerived";
import "./NivOverviewStats.css";

/** One segmented bar. Zero-value segments are dropped. */
function SegBar({ segments }) {
  const total = segments.reduce((sum, s) => sum + (s.value || 0), 0);
  if (!total) return <div className="nivos-bar nivos-bar-empty" aria-hidden="true" />;
  return (
    <div className="nivos-bar" role="img" aria-label={segments.map((s) => `${s.label}: ${s.value}`).join(", ")}>
      {segments
        .filter((s) => s.value > 0)
        .map((s, i) => (
          <span
            key={s.key}
            className={`nivos-seg nivos-tone-${s.tone}`}
            style={{ "--nivos-seg": `${(s.value / total) * 100}%`, "--nivos-i": i }}
            title={`${s.label}: ${s.display ?? s.value}`}
          />
        ))}
    </div>
  );
}

function Breakdown({ items }) {
  return (
    <ul className="nivos-breakdown">
      {items.map((item) => (
        <li key={item.key} className={`nivos-tone-${item.tone}`} title={`${item.label}: ${item.value}`}>
          <span className="nivos-dot" />
          <span className="nivos-bd-label">{item.label}</span>
          <strong className="nivos-bd-value">{item.value}</strong>
        </li>
      ))}
    </ul>
  );
}

function StatCard({ icon: Icon, tone, label, value, count, suffix, segments, breakdown, foot, index }) {
  const shown = useCountUp(Number.isFinite(count) ? count : 0);
  return (
    <article className={`nivos-card nivos-tone-${tone}`} style={{ "--nivos-i": index }}>
      <header className="nivos-card-head">
        <span className="nivos-card-icon">
          <Icon size={15} weight="fill" />
        </span>
        <span className="nivos-card-label">{label}</span>
      </header>
      <div className="nivos-card-value">
        <span className="nivos-card-num">{value !== undefined ? value : shown}</span>
        {suffix && <span className="nivos-card-suffix">{suffix}</span>}
      </div>
      {segments && <SegBar segments={segments} />}
      {breakdown && <Breakdown items={breakdown} />}
      {foot && <footer className="nivos-card-foot">{foot}</footer>}
    </article>
  );
}

function NivOverviewStats({ rows = [], inactiveCount = 0 }) {
  const s = useMemo(() => buildOverviewStats(rows, inactiveCount), [rows, inactiveCount]);
  const mrrShown = useCountUp(Math.round(s.money.netMonthly));
  const activePatientsShown = useCountUp(s.activePatients);

  const chips = [
    { key: "contact", icon: ChatCircleDots, tone: "violet", label: "דורשות קשר", value: s.needsContact },
    { key: "payments", icon: HandCoins, tone: "amber", label: "תשלומים לטיפול", value: s.paymentsDue },
    { key: "trial", icon: Clock, tone: "amber", label: "ניסיון מסתיים", value: s.trialEndingSoon },
    { key: "seats", icon: UsersThree, tone: "red", label: "חריגות מושבים", value: s.seatMismatches },
  ].filter((chip) => chip.value > 0);

  return (
    <div className="nivos">
      <div className="nivos-cards">
        <StatCard
          index={0}
          icon={Buildings}
          tone="brand"
          label='סה"כ קליניקות'
          count={s.total}
          suffix={`${s.newThisMonth} חדשות החודש`}
          segments={[
            { key: "paying", tone: "green", value: s.paying, label: "משלמות" },
            { key: "trial", tone: "amber", value: s.trial, label: "בניסיון" },
            { key: "churned", tone: "red", value: s.churned, label: "נטשו" },
            { key: "free", tone: "slate", value: s.free, label: "אחר" },
          ]}
          breakdown={[
            { key: "paying", tone: "green", label: "משלמות", value: s.paying },
            { key: "trial", tone: "amber", label: "בניסיון", value: s.trial },
            { key: "churned", tone: "red", label: "נטשו", value: s.churned },
            {
              key: "inactive",
              tone: "slate",
              label: s.inactiveByAdmin ? "מושבתות" : "אחר",
              value: s.inactiveByAdmin || s.free,
            },
          ]}
          foot={<span>המרה לתשלום {s.conversionRate}%</span>}
        />

        <StatCard
          index={1}
          icon={CurrencyCircleDollar}
          tone="green"
          label="MRR לפני מע״מ"
          value={formatShekels(mrrShown)}
          suffix="לחודש"
          segments={[
            { key: "monthly", tone: "green", value: s.money.fromMonthly, label: "מנויים חודשיים", display: formatShekels(s.money.fromMonthly) },
            { key: "oneTime", tone: "blue", value: s.money.fromOneTime, label: "חד-פעמי (פרוס לחודשים)", display: formatShekels(s.money.fromOneTime) },
          ]}
          breakdown={[
            {
              key: "monthly",
              tone: "green",
              label: `חודשי (${s.money.monthlyPayers})`,
              value: formatShekels(s.money.fromMonthly),
            },
            {
              key: "oneTime",
              tone: "blue",
              label: `חד-פעמי (${s.money.oneTimePayers}) · פרוס`,
              value: formatShekels(s.money.fromOneTime),
            },
            { key: "oneTimeSum", tone: "slate", label: "עסקאות חד-פעמיות פעילות", value: formatShekels(s.money.oneTimeActiveSum) },
            { key: "left", tone: "red", label: "עזבו", value: s.left },
          ]}
          foot={
            <>
              <span>שנתי {formatShekels(s.money.netYearly)}</span>
              <span>ממוצע ללקוח {formatShekels(s.money.arpa)}</span>
            </>
          }
        />

        <StatCard
          index={2}
          icon={Pulse}
          tone="blue"
          label="פעילות היום"
          count={s.activity.active}
          suffix={s.total ? `מתוך ${s.total}` : null}
          segments={[
            { key: "active", tone: "green", value: s.activity.active, label: "פעילות היום" },
            { key: "recent", tone: "blue", value: s.activity.recent, label: "פעילות לאחרונה" },
            { key: "week", tone: "amber", value: s.activity.week, label: "לפני שבוע" },
            { key: "inactive", tone: "orange", value: s.activity.inactive, label: "לא פעילות" },
            { key: "gone", tone: "red", value: s.activity.gone, label: "נעלמו" },
          ]}
          breakdown={[
            { key: "recent", tone: "blue", label: "פעילות ב-3 ימים", value: s.activity.recent },
            { key: "week", tone: "amber", label: "לפני שבוע", value: s.activity.week },
            { key: "inactive", tone: "orange", label: "לא פעילות 7+", value: s.activity.inactive },
            { key: "gone", tone: "red", label: "נעלמו 14+", value: s.activity.gone },
          ]}
          foot={
            <span>
              {s.total ? Math.round((s.activity.active / s.total) * 100) : 0}% מהקליניקות נכנסו היום
            </span>
          }
        />

        <StatCard
          index={3}
          icon={UsersThree}
          tone="violet"
          label="מטופלים פעילים"
          value={activePatientsShown.toLocaleString("he-IL")}
          suffix={s.totalPatients ? `מתוך ${s.totalPatients.toLocaleString("he-IL")}` : null}
          segments={[
            { key: "solo", tone: "violet", value: s.solo, label: "קליניקות סולו" },
            { key: "multi", tone: "blue", value: s.multi, label: "מרובות מטפלים" },
          ]}
          breakdown={[
            { key: "therapists", tone: "violet", label: "מטפלים פעילים", value: s.therapists },
            { key: "avg", tone: "blue", label: "ממוצע לקליניקה", value: s.avgPatients },
            { key: "solo", tone: "violet", label: "קליניקות סולו", value: s.solo },
            { key: "multi", tone: "blue", label: "מרובות מטפלים", value: s.multi },
          ]}
          foot={<span>{(s.totalPatients - s.activePatients).toLocaleString("he-IL")} מטופלים לא פעילים</span>}
        />
      </div>

      {chips.length > 0 && (
        <div className="nivos-chips">
          {chips.map((chip, i) => (
            <span key={chip.key} className={`nivos-chip nivos-tone-${chip.tone}`} style={{ "--nivos-i": i }}>
              <chip.icon size={13} weight="fill" />
              {chip.label}
              <strong>{chip.value}</strong>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default NivOverviewStats;
